import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { Field, FormSection, Radio, Reveal } from 'components';
import { formatDate } from 'helpers/date';
import QUESTIONS from 'containers/ma/Meetings/Application/questions';
import { RadioRow, NarrowInput } from 'containers/ma/Meetings/Application/Application.styled';

/**
 * The questions of the IPMA attendance form.
 *
 * Unlike the Member Association questionnaires this one has no fixed list: the
 * API describes it from the meetings that are open, one yes/no question per
 * meeting, and this renders whatever it was handed. The same component shows
 * the editable form and the read only view of a sent one.
 */
const AttendanceQuestions = ({ questions, dinner, answers, errors, disabled, onAnswer }) => {
  const { t } = useTranslation();

  const answerOf = (question) => answers[question];

  const yesNo = [
    { value: true, label: t('global.yes') },
    { value: false, label: t('global.no') },
  ];

  const renderChoices = (question, label, description) => (
    <Margin bottom="20" key={question}>
      <Field
        id={question}
        label={label}
        description={description}
        required
        error={errors[question]}
      >
        <RadioRow>
          {yesNo.map((option) => (
            <Radio
              key={`${question}-${String(option.value)}`}
              disabled={disabled}
              selected={answerOf(question) === option.value}
              onClick={() => onAnswer(question, option.value)}
            >
              {option.label}
            </Radio>
          ))}
        </RadioRow>
      </Field>
    </Margin>
  );

  return (
    <>
      <FormSection
        title={t('page.ipma.meetings.attendance.attendanceSection')}
        description={t('page.ipma.meetings.attendance.attendanceSectionHelp')}
      >
        {/* An FRT meeting is not scheduled on its own, so it arrives as a
            second question on the COD meeting it is held with. */}
        {questions.map((question) =>
          renderChoices(
            question.question,
            question.frt
              ? t('page.ipma.meetings.attendance.questionFrt', { meeting: question.name })
              : t('page.ipma.meetings.attendance.question', { meeting: question.name }),
            [question.typeLabel, question.date ? formatDate(question.date) : null]
              .filter(Boolean)
              .join(' - '),
          ),
        )}
      </FormSection>

      {/* The dinner is held alongside a meeting people travel to, so it is only
          asked when one of the open meetings is an onsite one. */}
      {dinner ? (
        <FormSection
          title={t('page.ma.meetings.application.dinnerSection')}
          description={t('page.ma.meetings.application.dinnerSectionHelp')}
        >
          {renderChoices(QUESTIONS.DINNER_PARTICIPATION, t('page.ma.meetings.application.dinner'))}
          {answerOf(QUESTIONS.DINNER_PARTICIPATION) === true && (
            <Reveal>
              <Margin bottom="20">
                <Field
                  id={QUESTIONS.DINNER_PLUS_ONE}
                  label={t('page.ma.meetings.application.dinnerPlusOne')}
                  description={t('page.ma.meetings.application.dinnerPlusOneHelp')}
                  error={errors[QUESTIONS.DINNER_PLUS_ONE]}
                >
                  <NarrowInput>
                    <input
                      id={QUESTIONS.DINNER_PLUS_ONE}
                      type="text"
                      disabled={disabled}
                      value={answerOf(QUESTIONS.DINNER_PLUS_ONE) || ''}
                      placeholder={t('page.ma.meetings.application.dinnerPlusOnePlaceholder')}
                      onChange={(event) => onAnswer(QUESTIONS.DINNER_PLUS_ONE, event.target.value)}
                    />
                  </NarrowInput>
                </Field>
              </Margin>
            </Reveal>
          )}
        </FormSection>
      ) : null}
    </>
  );
};

AttendanceQuestions.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      meetingId: PropTypes.number,
      name: PropTypes.string,
      typeLabel: PropTypes.string,
      date: PropTypes.string,
      frt: PropTypes.bool,
    }),
  ),
  dinner: PropTypes.bool,
  answers: PropTypes.shape({}),
  errors: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  onAnswer: PropTypes.func,
};

AttendanceQuestions.defaultProps = {
  questions: [],
  dinner: false,
  answers: {},
  errors: {},
  disabled: false,
  onAnswer: () => {},
};

export default AttendanceQuestions;
